import type { GrammarGladeChallenge, GrammarRole } from './GrammarGladeConfig.js';

export type GrammarCheckResult = 'incomplete' | 'order-mismatch' | 'order-correct'
  | 'roles-incomplete' | 'roles-mismatch' | 'punctuation-mismatch' | 'complete';

export interface GrammarChallengeProgress {
  readonly placedWordIds: readonly (string | null)[];
  readonly selectedWordId: string | null;
  readonly orderValidated: boolean;
  readonly roleAssignments: Partial<Record<GrammarRole, string>>;
  readonly selectedPunctuation: string | null;
  readonly hintStage: number;
  readonly attempts: number;
  readonly complete: boolean;
}

export interface GrammarGladeSerializedState {
  readonly version: 1;
  readonly challengeIndex: number;
  readonly completedChallengeIds: readonly string[];
  readonly challengeProgress: Readonly<Record<string, GrammarChallengeProgress>>;
}

const ROLES: readonly GrammarRole[] = ['noun', 'verb', 'adjective'];

function blank(challenge: GrammarGladeChallenge): GrammarChallengeProgress {
  return {
    placedWordIds: challenge.words.map(() => null), selectedWordId: null,
    orderValidated: false, roleAssignments: {}, selectedPunctuation: null,
    hintStage: 0, attempts: 0, complete: false,
  };
}

function sanitize(challenge: GrammarGladeChallenge, value: unknown): GrammarChallengeProgress {
  const candidate = value as Partial<GrammarChallengeProgress> | undefined;
  const known = new Set(challenge.words.map(word => word.id));
  const rawOrder = Array.isArray(candidate?.placedWordIds) ? candidate.placedWordIds : [];
  const order = challenge.words.map((_word, index) => {
    const id = rawOrder[index];
    return typeof id === 'string' && known.has(id) ? id : null;
  });
  const seen = new Set<string>();
  order.forEach((id, index) => { if (id && seen.has(id)) order[index] = null; else if (id) seen.add(id); });
  const assignments: Partial<Record<GrammarRole, string>> = {};
  ROLES.forEach(role => {
    const id = candidate?.roleAssignments?.[role];
    if (typeof id === 'string' && known.has(id)) assignments[role] = id;
  });
  const punctuation = typeof candidate?.selectedPunctuation === 'string'
    && challenge.punctuationOptions.includes(candidate.selectedPunctuation)
    ? candidate.selectedPunctuation : null;
  return {
    placedWordIds: order,
    selectedWordId: typeof candidate?.selectedWordId === 'string' && known.has(candidate.selectedWordId)
      ? candidate.selectedWordId : null,
    orderValidated: candidate?.orderValidated === true,
    roleAssignments: assignments,
    selectedPunctuation: punctuation,
    hintStage: Math.max(0, Math.min(challenge.hints.length, Number(candidate?.hintStage) || 0)),
    attempts: Math.max(0, Number(candidate?.attempts) || 0),
    complete: candidate?.complete === true,
  };
}

export class GrammarGladeState {
  private challengeIndex: number;
  private readonly progress: Record<string, GrammarChallengeProgress>;
  private readonly completed = new Set<string>();

  constructor(
    private readonly challenges: readonly GrammarGladeChallenge[],
    saved?: Partial<GrammarGladeSerializedState>,
  ) {
    if (challenges.length === 0) throw new RangeError('Grammar Glade needs challenges.');
    this.challengeIndex = Math.max(0, Math.min(challenges.length - 1, Number(saved?.challengeIndex) || 0));
    this.progress = {};
    challenges.forEach(challenge => {
      this.progress[challenge.id] = sanitize(challenge, saved?.challengeProgress?.[challenge.id]);
    });
    (saved?.completedChallengeIds ?? []).forEach(id => {
      if (challenges.some(challenge => challenge.id === id)) this.completed.add(id);
    });
  }

  get challenge() { return this.challenges[this.challengeIndex]; }
  get snapshot() { return this.progress[this.challenge.id]; }
  get mastered() { return this.completed.size === this.challenges.length; }
  get hasNextChallenge() { return this.challengeIndex < this.challenges.length - 1; }

  availableWordIds() {
    const placed = new Set(this.snapshot.placedWordIds.filter((id): id is string => id !== null));
    return this.challenge.words.map(word => word.id).filter(id => !placed.has(id));
  }

  wordForId(id: string | null) { return this.challenge.words.find(word => word.id === id); }

  selectWord(id: string) {
    if (this.snapshot.complete || !this.challenge.words.some(word => word.id === id)) return false;
    this.update({ selectedWordId: this.snapshot.selectedWordId === id ? null : id });
    return true;
  }

  activateSlot(index: number) {
    if (this.snapshot.complete || index < 0 || index >= this.snapshot.placedWordIds.length) return false;
    const order = [...this.snapshot.placedWordIds];
    const selected = this.snapshot.selectedWordId;
    if (selected) {
      const prior = order.indexOf(selected);
      if (prior >= 0) order[prior] = null;
      order[index] = selected;
      this.update({ placedWordIds: order, selectedWordId: null, orderValidated: false });
    } else if (order[index]) {
      const returned = order[index]; order[index] = null;
      this.update({ placedWordIds: order, selectedWordId: returned, orderValidated: false });
    } else return false;
    return true;
  }

  assignRole(role: GrammarRole) {
    const selected = this.snapshot.selectedWordId;
    if (!this.snapshot.orderValidated || !selected || !this.snapshot.placedWordIds.includes(selected)) return false;
    const assignments = { ...this.snapshot.roleAssignments };
    ROLES.forEach(key => { if (assignments[key] === selected) delete assignments[key]; });
    assignments[role] = selected;
    this.update({ roleAssignments: assignments });
    return true;
  }

  choosePunctuation(value: string) {
    if (!this.snapshot.orderValidated || !this.challenge.punctuationOptions.includes(value)) return false;
    this.update({ selectedPunctuation: value });
    return true;
  }

  check(): GrammarCheckResult {
    const current = this.snapshot;
    if (current.complete) return 'complete';
    if (current.placedWordIds.some(id => id === null)) return 'incomplete';
    if (!current.orderValidated) {
      this.bumpAttempt();
      if (!current.placedWordIds.every((id, index) => id === this.challenge.correctOrder[index])) return 'order-mismatch';
      this.update({ orderValidated: true, selectedWordId: null });
      return 'order-correct';
    }
    if (ROLES.some(role => !current.roleAssignments[role]) || !current.selectedPunctuation) return 'roles-incomplete';
    this.bumpAttempt();
    if (ROLES.some(role => current.roleAssignments[role] !== this.challenge.correctRoles[role])) return 'roles-mismatch';
    if (current.selectedPunctuation !== this.challenge.correctPunctuation) return 'punctuation-mismatch';
    this.update({ complete: true, selectedWordId: null });
    this.completed.add(this.challenge.id);
    return 'complete';
  }

  requestHint() {
    const index = Math.min(this.snapshot.hintStage, this.challenge.hints.length - 1);
    this.update({ hintStage: Math.min(this.challenge.hints.length, index + 1) });
    return this.challenge.hints[index];
  }

  nextChallenge() {
    if (!this.snapshot.complete || !this.hasNextChallenge) return false;
    this.challengeIndex += 1;
    return true;
  }

  resetCurrent() {
    if (this.snapshot.complete) return false;
    const hints = this.snapshot.hintStage;
    const attempts = this.snapshot.attempts;
    this.progress[this.challenge.id] = { ...blank(this.challenge), hintStage: hints, attempts };
    return true;
  }

  replay() {
    this.challengeIndex = 0;
    this.completed.clear();
    this.challenges.forEach(challenge => { this.progress[challenge.id] = blank(challenge); });
  }

  serialize(): GrammarGladeSerializedState {
    return {
      version: 1, challengeIndex: this.challengeIndex,
      completedChallengeIds: [...this.completed],
      challengeProgress: Object.fromEntries(this.challenges.map(challenge => [
        challenge.id,
        { ...this.progress[challenge.id], placedWordIds: [...this.progress[challenge.id].placedWordIds], roleAssignments: { ...this.progress[challenge.id].roleAssignments } },
      ])),
    };
  }

  private update(patch: Partial<GrammarChallengeProgress>) {
    this.progress[this.challenge.id] = { ...this.snapshot, ...patch };
  }

  private bumpAttempt() { this.update({ attempts: this.snapshot.attempts + 1 }); }
}
